import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef, WhiteBalancePreset } from '../../../domain';
import {
  DEFAULT_COLOR_CORRECT_STATE,
  buildColorCorrectRequest,
  hasSourceImage,
  type ColorCorrectFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

const WHITE_BALANCE_OPTIONS: WhiteBalancePreset[] = [
  'Auto',
  'Daylight',
  'Tungsten',
  'Fluorescent',
  'Cloudy',
  'Shade',
  'Custom',
];

const SLIDER_FIELDS: Array<{ key: keyof Omit<ColorCorrectFormState, 'whiteBalance' | 'model'>; label: string }> = [
  { key: 'exposure', label: 'Exposure' },
  { key: 'contrast', label: 'Contrast' },
  { key: 'saturation', label: 'Saturation' },
  { key: 'temperature', label: 'Temperature' },
  { key: 'tint', label: 'Tint' },
  { key: 'highlights', label: 'Highlights' },
  { key: 'shadows', label: 'Shadows' },
  { key: 'vibrance', label: 'Vibrance' },
];

export const ColorCorrectPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<ColorCorrectFormState>(DEFAULT_COLOR_CORRECT_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.colorCorrect);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to color-correct.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildColorCorrectRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <PaletteIcon fontSize="small" />
        <Typography variant="subtitle2">Color correct</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to color-correct.</Alert>
      )}

      <FormControl size="small" fullWidth>
        <InputLabel id="cc-wb-label">White balance</InputLabel>
        <Select
          labelId="cc-wb-label"
          label="White balance"
          value={state.whiteBalance}
          onChange={e =>
            setState(s => ({ ...s, whiteBalance: e.target.value as WhiteBalancePreset }))
          }
        >
          {WHITE_BALANCE_OPTIONS.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5 }}>
        {SLIDER_FIELDS.map(({ key, label }) => (
          <Box key={key}>
            <Typography variant="caption" color="text.secondary">
              {label} · {state[key].toFixed(2)}
            </Typography>
            <Slider
              value={state[key]}
              onChange={(_, v) =>
                setState(s => ({ ...s, [key]: Array.isArray(v) ? v[0] : v }))
              }
              min={-1}
              max={1}
              step={0.05}
              size="small"
            />
          </Box>
        ))}
      </Box>

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Color-correct failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <PaletteIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Correcting…' : 'Color correct'}
        </Button>
      </Box>
    </Stack>
  );
};
